import { percentOf } from '../../platform/percentOf'
import type { LocalDate } from '../../platform/localDate'
import type { Bill, Payment, Period, PeriodStatus } from './recurringWire'

export { addDays, localToday, type LocalDate } from '../../platform/localDate'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const pad = (n: number): string => String(n).padStart(2, '0')

function parts(d: LocalDate): [number, number, number] {
  const [y = 0, m = 0, day = 0] = d.split('-').map(Number)
  return [y, m, day]
}

/** Calendar-day difference (to − from). UTC arithmetic on the parts, so DST never shifts it. */
export function daysBetween(from: LocalDate, to: LocalDate): number {
  const [fy, fm, fd] = parts(from)
  const [ty, tm, td] = parts(to)
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000)
}

const plural = (n: number): string => `${n} ${n === 1 ? 'day' : 'days'}`

export function relativeDue(
  due: LocalDate,
  today: LocalDate,
): { kind: 'today' | 'future' | 'late'; days: number; text: string } {
  const diff = daysBetween(today, due)
  if (diff === 0) return { kind: 'today', days: 0, text: 'Due today' }
  if (diff > 0) return { kind: 'future', days: diff, text: `in ${plural(diff)}` }
  return { kind: 'late', days: -diff, text: `${plural(-diff)} late` }
}

/** P2 status groups, defined once so the header's "due" cannot drift from the sections. */
export const OVERDUE_STATUSES: readonly PeriodStatus[] = ['missed', 'unknown']
export const UPCOMING_STATUSES: readonly PeriodStatus[] = ['upcoming']
export const SKIPPED_STATUSES: readonly PeriodStatus[] = ['skipped', 'paused']
export const PAID_STATUSES: readonly PeriodStatus[] = ['paid']

const UNPAID: readonly PeriodStatus[] = [...OVERDUE_STATUSES, ...UPCOMING_STATUSES]

/** P3, matching iOS: active, unpaid, due before today, and not before this month began. */
export function isOverdue(
  bill: Bill,
  period: { dueDate: LocalDate; status: PeriodStatus },
  today: LocalDate,
): boolean {
  if (!bill.isActive || bill.isPaused) return false
  if (!UNPAID.includes(period.status)) return false
  const monthStart = `${today.slice(0, 7)}-01`
  return period.dueDate < today && period.dueDate >= monthStart
}

/**
 * Ruling 34: an UNNAMED Mark paid is offered only on the period the server
 * will pay. The server pays the period at `predicted_next_date` (`nextDue`)
 * unless the request names one, so a row due on any other date — a later
 * upcoming one, a past one behind a backlog — would be shown as "paid" while
 * another period was. The one named exception is a skipped period (#826, see
 * `canPaySkipped`), which is paid by its own date and never passes through here. Paid,
 * skipped and paused periods, and a paused or inactive bill (the same activity
 * test `isOverdue` applies), owe nothing.
 */
export function canMarkPaid(bill: Bill, entry: { dueDate: LocalDate; status: PeriodStatus } | undefined): boolean {
  return owesNextPeriod(bill, entry)
}

/**
 * Skip is offered on a period the server will skip, and a skip touches only the
 * period it names (#809). Two kinds of row qualify: any overdue period (past
 * due and unpaid, `missed` or `unknown`, however many sit behind it) and the
 * period due on `nextDue`. Paid, skipped and paused periods and a paused or
 * inactive bill are not offered. A bill with no `nextDue` is not offered
 * either: the backend's `_record_skip` would give it a due date it does not
 * have. `canSkip` reads the server's status, which the server dates by UTC, so
 * a row can read `missed` or `unknown` a local day early. Separate from
 * `canMarkPaid`, which still accepts only the `nextDue` period: an overdue row
 * behind it can be skipped but not paid (#809, owner). The skip request
 * carries the row's own `dueDate`, never `nextDue`.
 */
export function canSkip(bill: Bill, entry: { dueDate: LocalDate; status: PeriodStatus } | undefined): boolean {
  if (bill.nextDue === null || !isOpen(bill, entry)) return false
  return OVERDUE_STATUSES.includes(entry.status) || entry.dueDate === bill.nextDue
}

/**
 * #826: a SKIPPED period can still be paid, and it is paid by its own date
 * (`period_due_date`), never `nextDue`: paying it moves nothing else. Only a
 * period the server marked `skipped` on an active, unpaused bill qualifies.
 * An overdue row behind the next due payment stays Skip-only (#809, owner):
 * `missed` and `unknown` are not skipped, so `canMarkPaid` still refuses them.
 */
export function canPaySkipped(bill: Bill, entry: { dueDate: LocalDate; status: PeriodStatus } | undefined): boolean {
  return bill.isActive && !bill.isPaused && entry !== undefined && entry.status === 'skipped'
}

/** The period a Mark paid on this row names, or null when the server's `nextDue` is the period. */
export function payPeriodOf(bill: Bill, entry: { dueDate: LocalDate; status: PeriodStatus } | undefined): LocalDate | null {
  return entry !== undefined && canPaySkipped(bill, entry) ? entry.dueDate : null
}

function isOpen(
  bill: Bill,
  entry: { dueDate: LocalDate; status: PeriodStatus } | undefined,
): entry is { dueDate: LocalDate; status: PeriodStatus } {
  if (!bill.isActive || bill.isPaused || entry === undefined) return false
  return entry.status !== 'paid' && entry.status !== 'skipped' && entry.status !== 'paused'
}

function owesNextPeriod(bill: Bill, entry: { dueDate: LocalDate; status: PeriodStatus } | undefined): boolean {
  return bill.nextDue !== null && isOpen(bill, entry) && entry.dueDate === bill.nextDue
}

export type SectionKey = 'overdue' | 'upcoming' | 'skipped' | 'paid'

/**
 * Which month-view section an entry belongs to — the ONE overdue predicate.
 *
 * - Paid and skipped come from the server's status groups.
 * - An unpaid entry of a paused bill is Skipped: nothing is owed while paused.
 * - An unpaid entry in the CURRENT month (or later) is Overdue exactly when
 *   `isOverdue` (P3) says so: active, not paused, due before the user's local
 *   today and not before this month began. The server's own `missed` flag is
 *   NOT consulted for the split, because it is dated by UTC (S17): at 20:00 in
 *   Vancouver on the due date it already says missed (Review Focus 2).
 *   **So `monthSections(currentMonth).overdue.count` is the badge rule** —
 *   Task 16's nav badge must read it, not re-derive it.
 * - A PAST month's unpaid, past-due entry is Overdue too, although P3's
 *   month floor would say no: the month view is per month (S7: an unpaid June
 *   period appears only in June), and in June's own view that period was
 *   never paid. It never counts toward the badge, which reads the current
 *   month only.
 * - Unpaid and not yet due (today included): Upcoming.
 * - The one case left — a past-due period this month of an INACTIVE bill,
 *   which `isOverdue` excludes — owes nothing, so it is listed under Skipped
 *   rather than shown as "upcoming" with "3 days late" beside it.
 */
export function sectionOf(
  e: { bill: Bill; dueDate: LocalDate; status: PeriodStatus },
  today: LocalDate,
): SectionKey {
  if (PAID_STATUSES.includes(e.status)) return 'paid'
  if (SKIPPED_STATUSES.includes(e.status) || e.bill.isPaused) return 'skipped'
  if (isOverdue(e.bill, e, today)) return 'overdue'
  const monthStart = `${today.slice(0, 7)}-01`
  if (e.dueDate < monthStart) return 'overdue'
  return e.dueDate < today ? 'skipped' : 'upcoming'
}

/** S6. 'unknown' is not missed: there is nothing to say about it. */
export function settledAs(period: Period): 'paid' | 'paid_late' | 'missed' | null {
  if (period.status === 'missed') return 'missed'
  if (period.status === 'paid' && period.payment) {
    return period.payment.paidOn <= period.dueDate ? 'paid' : 'paid_late'
  }
  return null
}

/** What the period sheet itself has done to a period that the timeline may not show yet. */
export type SheetWrites = {
  /** The payment a link just filed under this period. */
  filed: Payment | null
  /** Every payment this sheet has unlinked, in any order. */
  unlinkedIds: readonly string[]
}

/**
 * The period sheet's period: the live timeline entry, corrected by the sheet's
 * own writes until a refetch agrees (iOS `currentRow`).
 *
 * - A payment the sheet unlinked is never shown, however stale the timeline is
 *   — which is why this takes a SET of ids: unlink, relink, and the timeline
 *   can still hold the first payment while the second is filed.
 * - The live payment wins when it is still linked: it is the server's word.
 *   Otherwise the payment just filed here.
 * - A shown payment makes the period `paid`; whether that reads Paid or Paid
 *   late is `settledAs` (paidOn against dueDate), exactly as history decides.
 * - A period whose payment was unlinked is unpaid: `missed` once its due date
 *   has passed, `upcoming` on or before it (local today, S17).
 * - Anything else is the timeline untouched — an optimistic cash flip (`paid`,
 *   payment on its way) included.
 */
export function periodAsShown(period: Period, writes: SheetWrites, today: LocalDate): Period {
  const linked = (p: Payment | null): p is Payment => p !== null && !writes.unlinkedIds.includes(p.id)
  const payment = linked(period.payment) ? period.payment : linked(writes.filed) ? writes.filed : null
  if (payment !== null) return { ...period, status: 'paid', payment }
  if (period.payment !== null) return { ...period, status: period.dueDate < today ? 'missed' : 'upcoming', payment: null }
  return period
}

export function onTime(periods: Period[]): { onTime: number; settled: number } {
  let onTimeCount = 0
  let settled = 0
  for (const p of periods) {
    const s = settledAs(p)
    if (s === null) continue
    settled += 1
    if (s === 'paid') onTimeCount += 1
  }
  return { onTime: onTimeCount, settled }
}

export function formatDue(d: LocalDate, style: 'short' | 'long'): string {
  const [y, m, day] = parts(d)
  const month = MONTHS[m - 1] ?? ''
  return style === 'long' ? `${day} ${month} ${y}` : `${month.slice(0, 3)} ${day}`
}

export type AmountChange = { direction: 'up' | 'down'; deltaCents: number; percent: number }

/** A change counts only when it is strictly more than this share of the previous amount. */
const CHANGE_THRESHOLD_DENOMINATOR = 20 // 5% as an integer ratio, so 105.00 vs 100.00 is exact

/**
 * The last two payments (ordered by `dueDate`, null amounts skipped) compared:
 * the latest against the one before it, never against the first. Missed periods
 * between them are not payments and are ignored. Null when there are fewer than
 * two, the previous amount is 0, or |Δ| is not more than 5% of the previous.
 */
export function amountChange(periods: Period[]): AmountChange | null {
  const paid = [...periods]
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0))
    .flatMap((p) => (p.payment?.amountCents == null ? [] : [p.payment.amountCents]))
  const latest = paid.at(-1)
  const previous = paid.at(-2)
  if (latest === undefined || previous === undefined || previous === 0) return null
  const delta = latest - previous
  if (Math.abs(delta) * CHANGE_THRESHOLD_DENOMINATOR <= previous) return null
  return {
    direction: delta > 0 ? 'up' : 'down',
    deltaCents: Math.abs(delta),
    percent: percentOf(Math.abs(delta), previous),
  }
}


export type PassbookKey = 'overdue' | 'upcoming' | 'paid' | 'paused' | 'income'

/**
 * iOS's "Paid this month": the stream's last payment fell in the current local
 * month AND its next due date is the first of next month or later, so this
 * cycle is settled. A stream with no last payment or no next due is not.
 */
export function paidThisMonth(bill: Bill, today: LocalDate): boolean {
  if (bill.lastDate === null || bill.nextDue === null) return false
  if (bill.lastDate.slice(0, 7) !== today.slice(0, 7)) return false
  const [y, m] = parts(today)
  const nextMonthStart = m === 12 ? `${y + 1}-01-01` : `${y}-${pad(m + 1)}-01`
  return bill.nextDue >= nextMonthStart
}

/**
 * Which Passbook section a stream sits in, or null when it appears nowhere.
 * One row per stream, so exactly one section.
 *
 * - Removed bills, and bills that are neither active nor paused (a
 *   `cancelled` status, which iOS hides) are nowhere.
 * - Paused wins over Income: a paused inflow is listed under Paused.
 * - Inflows are Income, never Overdue/Upcoming/Paid (D6).
 * - Otherwise `isOverdue` (P3) against `nextDue`, then the iOS paid rule,
 *   else Upcoming.
 * - An unpaid stream whose `nextDue` fell in an EARLIER month is not Overdue
 *   (P3's month floor) and lands in Upcoming: carried-over periods stay in
 *   their normal section, as on iOS (S7).
 */
export function passbookSection(bill: Bill, today: LocalDate): PassbookKey | null {
  if (bill.isRemoved) return null
  if (bill.isPaused) return 'paused'
  if (!bill.isActive) return null
  if (bill.isInflow) return 'income'
  if (bill.nextDue !== null && isOverdue(bill, { dueDate: bill.nextDue, status: 'upcoming' }, today)) return 'overdue'
  return paidThisMonth(bill, today) ? 'paid' : 'upcoming'
}
